/* eslint-disable react/prop-types -- props は TypeScript の interface で定義 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { HomeIcon } from "../../common/icons";
import {
  CustomSectionTitle,
  CustomButton,
  CustomLoading,
  CustomCheckbox,
  CustomFormNavigationGuard,
  colors,
} from "@ippon/shared-components";
import { CustomListToolbar } from "../ListToolbar/CustomListToolbar";
import RepairCreateDrawer from "../PropertyDetail/PropertyRepairsList/RepairCreateDrawer";
import { useForm } from "react-hook-form";
import "./PropertyList.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
// PropertySection は Unit に統合された
import { fetchUnitsByProperty } from "@/store/unitSlice";
// import ipponApi from "../../config/axios";
import { searchProperties, deleteProperty } from "../../store/propertySlice";
import { BuildingIcon, ResidenceIcon } from "../../common/icons";
import { CustomModal } from "@ippon/shared-components";
import { useGlobalToast } from "../../hooks/useGlobalToast";
import { useCan } from "@/hooks/useCan";
import { Can } from "@/components/auth/Can";
import { Pagination } from "./Pagination";
// 旧 PropertyUnitList は廃止 (Unit に統合された)
import { PropertyUnitList } from "./PropertyUnitList";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
// ========================================
// Types
// ========================================
type PerPageForm = { perPage: number };

type PropertySummary = {
  id: string;
  name: string;
  totalUnits: number;
  privateRooms: number;
  estimates: number;
  property_category?: string; // レジデンス/ビルディング (アイコン切替用)
};

// ========================================
// Mock Data (temporary)
// ========================================
const mockProperties: PropertySummary[] = [];
const EMPTY_SECTIONS: any[] = [];

const PropertyEmptyIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g clipPath="url(#property-empty-icon-clip)">
      <path
        d="M6 11.3333H5.33333C5.33333 11.5101 5.40357 11.6797 5.5286 11.8047C5.65362 11.9298 5.82319 12 6 12V11.3333ZM11.3333 11.3333V12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333H11.3333ZM11.3333 8H12C12.0002 7.91239 11.983 7.82561 11.9496 7.74463C11.9162 7.66364 11.8672 7.59002 11.8053 7.528L11.3333 8ZM8.66667 5.33333L9.13867 4.86133C9.07674 4.79925 9.00317 4.74999 8.92218 4.71638C8.84118 4.68277 8.75436 4.66547 8.66667 4.66547C8.57898 4.66547 8.49215 4.68277 8.41115 4.71638C8.33016 4.74999 8.25659 4.79925 8.19467 4.86133L8.66667 5.33333ZM6 8L5.528 7.528C5.46613 7.59002 5.41709 7.66364 5.38369 7.74463C5.35029 7.82561 5.33318 7.91239 5.33333 8H6ZM19.8053 18.8613L14.472 13.528L13.528 14.472L18.8613 19.8053L19.8053 18.8613ZM8.66667 16C6.72175 16 4.85648 15.2274 3.48122 13.8521C2.10595 12.4768 1.33333 10.6116 1.33333 8.66667H0C0 10.9652 0.913093 13.1696 2.53841 14.7949C4.16372 16.4202 6.36812 17.3333 8.66667 17.3333V16ZM16 8.66667C16 10.6116 15.2274 12.4768 13.8521 13.8521C12.4768 15.2274 10.6116 16 8.66667 16V17.3333C10.9652 17.3333 13.1696 16.4202 14.7949 14.7949C16.4202 13.1696 17.3333 10.9652 17.3333 8.66667H16ZM8.66667 1.33333C10.6116 1.33333 12.4768 2.10595 13.8521 3.48122C15.2274 4.85648 16 6.72175 16 8.66667H17.3333C17.3333 6.36812 16.4202 4.16372 14.7949 2.53841C13.1696 0.913093 10.9652 0 8.66667 0V1.33333ZM8.66667 0C6.36812 0 4.16372 0.913093 2.53841 2.53841C0.913093 4.16372 0 6.36812 0 8.66667H1.33333C1.33333 6.72175 2.10595 4.85648 3.48122 3.48122C4.85648 2.10595 6.72175 1.33333 8.66667 1.33333V0ZM6 12H11.3333V10.6667H6V12ZM12 11.3333V8H10.6667V11.3333H12ZM11.8053 7.528L9.13867 4.86133L8.19467 5.80533L10.8613 8.472L11.8053 7.528ZM8.19467 4.86133L5.528 7.528L6.472 8.472L9.13867 5.80533L8.19467 4.86133ZM5.33333 8V11.3333H6.66667V8H5.33333Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="property-empty-icon-clip">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// ========================================
// Memoized Accordion Item
// ========================================
const ACCORDION_SX = {
  flex: 1,
  minWidth: 0,
  border: "1px solid #d9d9d9",
  borderRadius: "10px",
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": {
    borderRadius: "10px",
    margin: 0,
  },
} as const;

const ACCORDION_SUMMARY_SX = {
  px: 3,
  py: 0,
  minHeight: 50,
  borderRadius: "10px",
  "& .MuiAccordionSummary-content": { margin: "7px 0" },
  "&.Mui-expanded": { borderRadius: "10px 10px 0 0", minHeight: 50 },
  "&.Mui-expanded .MuiAccordionSummary-content": { margin: "7px 0" },
} as const;

const ACCORDION_TRANSITION = { timeout: 180, unmountOnExit: true } as const;

const REPAIR_BUTTON_SX = {
  height: 36,
  borderRadius: "10px",
  px: 2,
  backgroundColor: "#f7fbfd",
  borderColor: "#0B9DBD",
  color: "#0B9DBD",
  fontFamily: '"Noto Sans JP"',
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "170%",
  textTransform: "none",
  "&:hover": { backgroundColor: "#e9f6fa", borderColor: "#0B9DBD" },
} as const;

interface PropertyAccordionItemProps {
  property: PropertySummary;
  expanded: boolean;
  selected: boolean;
  privateSections: any[];
  navigate: (path: string, options?: any) => void;
  onToggleExpand: (id: string, isExpanded: boolean) => void;
  onToggleSelect: (id: string, checked: boolean) => void;
  onRepairClick: (id: string) => void;
}

const PropertyAccordionItem: React.FC<PropertyAccordionItemProps> = React.memo(
  ({
    property: p,
    expanded,
    selected,
    privateSections,
    navigate,
    onToggleExpand,
    onToggleSelect,
    onRepairClick,
  }) => {
    const handleChange = useCallback(
      (_e: unknown, isExpanded: boolean) => onToggleExpand(p.id, isExpanded),
      [onToggleExpand, p.id]
    );

    const handleCheckboxChange = useCallback(() => {
      onToggleSelect(p.id, !selected);
    }, [onToggleSelect, p.id, selected]);

    const handleNameClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/properties/${p.id}`, { state: { propName: p.name } });
      },
      [navigate, p.id, p.name]
    );

    const handleRepair = useCallback(
      (e: any) => {
        e.stopPropagation();
        onRepairClick(p.id);
      },
      [onRepairClick, p.id]
    );

    return (
      <Accordion
        expanded={expanded}
        onChange={handleChange}
        slotProps={{ transition: ACCORDION_TRANSITION }}
        sx={ACCORDION_SX}
      >
        <AccordionSummary expandIcon={null} sx={ACCORDION_SUMMARY_SX}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            width="100%"
            minWidth={0}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ flexShrink: 0 }}
              >
                <CustomCheckbox
                  checked={selected}
                  onChange={handleCheckboxChange}
                  size="small"
                />
              </Box>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: expanded ? "#DBF8FF" : "transparent",
                  color: expanded ? "#0E9DBC" : "#3e3e3e",
                  transition: "all 0.2s",
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: "#DBF8FF",
                    "& svg": { color: "#0E9DBC" },
                  },
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                    fontSize: 20,
                    color: "inherit",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  ml: 1,
                  flexShrink: 0,
                }}
              >
                {p.property_category === "residence" ? (
                  <ResidenceIcon />
                ) : (
                  <BuildingIcon />
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: "100%",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                <Box
                  component="span"
                  onClick={handleNameClick}
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    fontFamily: '"Noto Sans JP"',
                    color: "#1f2937",
                    lineHeight: "170%",
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": { color: "#0b9dbd" },
                  }}
                >
                  {p.name}
                </Box>
              </Box>
            </Stack>
            <Can action="repair:edit">
              <CustomButton
                label="工事登録"
                variant="outlined"
                buttonCategory="submit"
                className="repair-work-button"
                sx={REPAIR_BUTTON_SX}
                onClick={handleRepair}
              />
            </Can>
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{ pt: 0, pb: 1, borderRadius: "0 0 10px 10px" }}
        >
          <Box
            px={1.5}
            py={1}
            sx={{ backgroundColor: "#f9fafb", borderRadius: "8px" }}
          >
            <PropertyUnitList
              key={`private-${p.id}`}
              icon={<HomeIcon />}
              title="専有部"
              count={privateSections.length}
              unit="部屋"
              items={privateSections}
              propertyId={p.id}
              propertyName={p.name}
              navigate={navigate as any}
              areaType="private"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }
);
PropertyAccordionItem.displayName = "PropertyAccordionItem";

export const PropertyList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const can = useCan();
  const {
    data: searchedProperties,
    loading: namesLoading,
    loaded: searchedLoaded,
  } = useSelector(
    (state: RootState) => state.property.searched,
  );

  // 型安全性のためのヘルパー
  const propertyItems = searchedProperties?.items || [];
  const totalCount = searchedProperties?.total || 0;
  // keep form instance if needed later for consistency with other pages
  const {
    register,
    formState: { errors, isDirty },
    reset,
    getValues,
  } = useForm<{ keyword: string }>({
    defaultValues: { keyword: "" },
  });

  const { control: perPageControl } = useForm<PerPageForm>({
    defaultValues: { perPage: 50 },
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openRepairModal, setOpenRepairModal] = useState<boolean>(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activePropertyId, setActivePropertyId] = useState<string | null>(null);
  const [appliedKeyword, setAppliedKeyword] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);
  const [initialLoadError, setInitialLoadError] =
    useState<ApiErrorType | null>(null);

  const perPageOptions = useMemo(
    () => [
      { value: 10, label: "10件" },
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" },
    ],
    [],
  );

  const { showToast } = useGlobalToast();

  const fetchDetail = useCallback(async () => {
    setInitialLoadError(null);
    const params = {
      objectName: appliedKeyword,
      page: String(currentPage),
      limit: String(itemsPerPage),
    };
    const result = await dispatch(searchProperties(params) as any);
    if (searchProperties.rejected.match(result)) {
      const payload = result.payload as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
    }
  }, [dispatch, currentPage, itemsPerPage, appliedKeyword]);

  // fetch properties with pagination
  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // map store data to displayable list
  const propertiesFromStore: PropertySummary[] = useMemo(() => {
    return propertyItems
      .map((item: any) => {
        const derivedName =
          item?.name ?? item?.label ?? (typeof item === "string" ? item : "");
        const derivedId = item?.id ?? item?.value ?? item?.slug ?? "";
        if (!derivedName) return null;
        return {
          id: String(derivedId),
          name: String(derivedName),
          totalUnits: 0,
          privateRooms: 0,
          estimates: 0,
          property_category: item?.property_category,
        } as PropertySummary;
      })
      .filter(Boolean) as PropertySummary[];
  }, [propertyItems]);

  const dataSource: PropertySummary[] =
    propertiesFromStore.length > 0 ? propertiesFromStore : mockProperties;

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / itemsPerPage) : 0;

  // remove forced initial expanded panel to allow free open/close

  // モーダルを閉じる処理
  const handleCloseRepairModal = () => {
    setOpenRepairModal(false);
    setActivePropertyId(null);
  };

  // サーバーサイドでフィルタリング済みなので、そのまま使用
  const filtered = dataSource;
  const hasPropertyListCache = Boolean(searchedLoaded) || dataSource.length > 0;
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasPropertyListCache,
    dedupeKey: `properties:${appliedKeyword}:${currentPage}:${itemsPerPage}`,
  });

  // uncontrolled accordions; no explicit expanded state
  const [propertyIdToPrivateSections, setPropertyIdToPrivateSections] =
    useState<Record<string, any[]>>({});

  const handleToggleSelect = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleToggleExpand = useCallback(
    (id: string, isExpanded: boolean) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (isExpanded) next.add(id);
        else next.delete(id);
        return next;
      });
      if (isExpanded) {
        // 専有部のみ取得 (一覧アコーディオンは専有部のみ表示)
        dispatch(
          fetchUnitsByProperty({ property_id: id, area_type: "private" }),
        )
          .unwrap()
          .then((rows: any) => {
            const list = Array.isArray(rows) ? rows : [];
            setPropertyIdToPrivateSections((prev) => ({ ...prev, [id]: list }));
          })
          .catch(() => {
            setPropertyIdToPrivateSections((prev) => ({ ...prev, [id]: [] }));
          });
      }
    },
    [dispatch]
  );

  const handleRepairClick = useCallback((id: string) => {
    setActivePropertyId(id);
    setOpenRepairModal(true);
  }, []);

  // ページ変更ハンドラー
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedIds(new Set()); // ページ変更時に選択をクリア
  };

  // 表示件数変更ハンドラー
  const handlePerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // 1ページ目に戻す
  };

  // 検索実行時にページを1にリセット
  const handleSearch = () => {
    const kw = (getValues("keyword") || "").toString();
    setAppliedKeyword(kw);
    setCurrentPage(1);
  };

  const handleClear = () => {
    reset();
    setAppliedKeyword("");
    setCurrentPage(1);
  };

  // 削除モーダルを開く
  const handleOpenDeleteModal = () => {
    if (selectedIds.size === 0) {
      showToast({
        type: "warning",
        message: "削除する物件を選択してください",
      });
      return;
    }
    setOpenDeleteModal(true);
  };

  // 物件削除処理
  // モーダル内で「削除」ボタンの文字部分が loading スピナーに変わり、
  // 成功 → モーダルクローズ + toast、失敗 → モーダルそのまま + toast
  const handleDeleteProperties = async () => {
    setIsDeleting(true);

    try {
      const formattedDataToDelete = {
        propIds: Array.from(selectedIds),
      };

      const action = await dispatch(
        deleteProperty(formattedDataToDelete) as any,
      );

      if (action.type === "properties/delete/fulfilled") {
        // 成功時にモーダルクローズ
        setOpenDeleteModal(false);
        showToast({
          type: "success",
          message: `物件の削除が完了しました`,
        });

        // 選択状態をクリア
        setSelectedIds(new Set());

        // 物件リストを再取得
        const params = {
          objectName: appliedKeyword,
          page: String(currentPage),
          limit: String(itemsPerPage),
        };
        dispatch(searchProperties(params) as any);
      } else {
        // 失敗時はモーダルを閉じずに toast のみ
        const errorMessage =
          action.payload?.message || "物件の削除に失敗しました";
        showToast({
          type: "error",
          message: `削除エラー: ${errorMessage}`,
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      // 例外時もモーダルを閉じない
      showToast({
        type: "error",
        message: "物件の削除中にエラーが発生しました",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const isFormChanged = isDirty;

  if (shouldShowInitialLoadError(initialLoadError, hasPropertyListCache)) {
    return (
      <InitialLoadError
        errorType={initialLoadError}
        onRetry={fetchDetail}
      />
    );
  }

  return (
    <CustomFormNavigationGuard
      hasFormChanged={isFormChanged}
    >
      {() => (
        <Box
          pt={{ xs: "10px", sm: "24px" }}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <CustomSectionTitle title="物件一覧" size="large" />

          <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
            <CustomListToolbar
              clearIconHideBetween600And900={true}
              showSearch={true}
              searchPlaceholder="物件名を入力"
              searchRegister={register}
              searchErrors={errors}
              searchName="keyword"
              onSearch={handleSearch}
              onClear={handleClear}
              perPageOptions={perPageOptions}
              perPageControl={perPageControl}
              onPerPageChange={handlePerPageChange}
              primaryButtonLabel="物件登録"
              primaryButtonOnClick={() => navigate("/properties/create")}
              showPrimaryButton={can("property:edit")}
              deleteDisabled={isDeleting || selectedIds.size === 0}
              onDeleteClick={handleOpenDeleteModal}
              showDelete={can("property:delete")}
            />
          </Box>

          {namesLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                width: "100%",
              }}
            >
              <CustomLoading loading={true} fullScreen={false} size="medium" />
            </Box>
          ) : filtered.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                minHeight: "160px",
              }}
            >
              <Box sx={{ color: colors.border.light, mb: 1, lineHeight: 0 }}>
                <PropertyEmptyIcon />
              </Box>
              <Typography
                sx={{
                  textAlign: "center",
                  color: colors.text.secondary,
                  fontSize: "14px",
                }}
              >
                物件が登録されていません
              </Typography>
            </Box>
          ) : (
            <Box mt={1} sx={{ minWidth: 0, width: "100%" }}>
              {filtered.map((p) => (
                <Box key={p.id} mb={1}>
                  <PropertyAccordionItem
                    property={p}
                    expanded={expandedIds.has(p.id)}
                    selected={selectedIds.has(p.id)}
                    privateSections={
                      propertyIdToPrivateSections[p.id] || EMPTY_SECTIONS
                    }
                    navigate={navigate}
                    onToggleExpand={handleToggleExpand}
                    onToggleSelect={handleToggleSelect}
                    onRepairClick={handleRepairClick}
                  />
                </Box>
              ))}

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </Box>
          )}
          {activePropertyId && (
            <RepairCreateDrawer
              open={openRepairModal}
              onClose={handleCloseRepairModal}
              propertyId={activePropertyId}
            />
          )}

          {/* Delete Confirmation Modal */}
          <CustomModal
            title="削除の確認"
            openModal={openDeleteModal}
            handleCloseModal={() => {
              if (!isDeleting) setOpenDeleteModal(false);
            }}
            bodyText=""
            hideCloseButton
            modalType="deleteModal"
          >
            <Box>
              <Typography sx={{ fontSize: "14px", color: "#666" }}>
                物件を削除すると関連する全ての契約、見積情報が削除されます。
                <br />
                選択した物件を削除してもよろしいでしょうか？
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: "10px" }}
            >
              <CustomButton
                label="戻る"
                buttonCategory="cancelModal"
                onClick={() => setOpenDeleteModal(false)}
                disabled={isDeleting}
              />
              <Box
                sx={{
                  position: "relative",
                  // 元の deleteModalButton と同じく flex 50% を確保
                  width: "50%",
                  // 内部の CustomButton は wrapper の幅いっぱいに
                  "& .MuiButton-root": { width: "100% !important" },
                  // 削除中は文字色を透明にして spinner を重ねる
                  ...(isDeleting && {
                    "& .MuiButton-root": {
                      width: "100% !important",
                      color: "transparent !important",
                    },
                  }),
                }}
              >
                <CustomButton
                  label="削除"
                  onClick={handleDeleteProperties}
                  buttonCategory="deleteModal"
                  disabled={isDeleting}
                />
                {isDeleting && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  </Box>
                )}
              </Box>
            </Box>
          </CustomModal>
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};
